import React from 'react';

/**
 * Subject icon picker. Maps a subject name + the student's grade number to
 * the right Figma-sourced icon under public/lesson-icons/. Some subjects
 * exist on every level (Matematik, Türkçe, Fen Bilimleri) — there's a
 * separate icon per level for those. Branch-only subjects (Kimya, Fizik,
 * Biyoloji, Coğrafya, Tarih, İnkılap, Felsefe, Geometri) only have a
 * Lise icon. Sosyal Bilgiler is Ortaokul only.
 *
 * If no match is found we render a neutral placeholder so the layout
 * doesn't break — book covers, generic emoji, etc. should all keep
 * rendering.
 *
 * Figma source: file 6nrQCBxlgi5ODYgeroTZk8 node 855-4403 (BUGFİX canvas)
 */

export type SchoolLevel = 'ilkokul' | 'ortaokul' | 'lise';

/** Normalize Turkish subject names to a canonical key. */
function normalizeSubject(raw: string | null | undefined): string {
  if (!raw) return '';
  const s = raw.toLocaleLowerCase('tr-TR').trim();
  if (s.includes('matematik')) return 'matematik';
  if (s.includes('türk dili') || s.includes('türk dili ve edebiyat') || s.includes('edebiyat')) return 'turk-dili-edebiyat';
  if (s.includes('türkçe')) return 'turkce';
  if (s.includes('fen bilim') || s === 'fen') return 'fen';
  if (s.includes('biyoloji')) return 'biyoloji';
  if (s.includes('fizik')) return 'fizik';
  if (s.includes('kimya')) return 'kimya';
  if (s.includes('coğrafya') || s.includes('cografya')) return 'cografya';
  if (s.includes('inkılap') || s.includes('inkilap')) return 'inkilap';
  if (s.includes('sosyal')) return 'sosyal';
  if (s.includes('tarih')) return 'tarih';
  if (s.includes('felsefe')) return 'felsefe';
  if (s.includes('geometri')) return 'geometri';
  return '';
}

/** Map a grade number (1-12) or null to a school level bucket. */
export function levelFromGrade(grade: number | null | undefined): SchoolLevel {
  if (grade != null && grade >= 1 && grade <= 4) return 'ilkokul';
  if (grade != null && grade >= 5 && grade <= 8) return 'ortaokul';
  // 9-12, TYT, AYT, null (TYT/AYT prep books) all → lise.
  return 'lise';
}

/** Resolve the public path for a subject icon, or null when no asset exists. */
export function lessonIconPath(subject: string | null | undefined, level: SchoolLevel): string | null {
  const key = normalizeSubject(subject);
  if (!key) return null;

  // Multi-level subjects have a unique icon per level.
  // PNG, not SVG: the committed .svg files are Figma pattern-fill wrappers
  // around embedded PNG rasters (no real vectors), and several exports are
  // broken — tarih/biyoloji/fizik/cografya/edebiyat wrap full-size opaque
  // illustration frames (biyoloji + fizik even embed the SAME image), which
  // render as gray blocks on the faded card backgrounds. The .png twins are
  // the correct transparent cut-out icons.
  if (key === 'matematik') return `/lesson-icons/matematik-${level}.png`;
  if (key === 'fen') {
    if (level === 'lise') return null; // Fen Bilimleri only ilkokul/ortaokul
    return `/lesson-icons/fen-${level}.png`;
  }
  if (key === 'turkce') {
    // Türkçe = ilkokul/ortaokul; lise version is "Türk Dili ve Edebiyatı".
    if (level === 'lise') return '/lesson-icons/turk-dili-edebiyat-lise.png';
    return `/lesson-icons/turkce-${level}.png`;
  }
  if (key === 'turk-dili-edebiyat') return '/lesson-icons/turk-dili-edebiyat-lise.png';

  // Single-level subjects.
  const singleLevel: Record<string, SchoolLevel> = {
    biyoloji: 'lise',
    fizik: 'lise',
    kimya: 'lise',
    cografya: 'lise',
    inkilap: 'lise',
    tarih: 'lise',
    felsefe: 'lise',
    geometri: 'lise',
    sosyal: 'ortaokul',
  };
  const targetLevel = singleLevel[key];
  if (!targetLevel) return null;
  // Render even if the student's level doesn't match — the subject still has
  // ONE icon and that's the correct asset (e.g. a teacher viewing a Lise
  // class while flagged as ortaokul-level).
  return `/lesson-icons/${key}-${targetLevel}.png`;
}

interface Props {
  subject: string | null | undefined;
  /** Either a grade number (1-12) or a pre-resolved level bucket. */
  grade?: number | null;
  level?: SchoolLevel;
  size?: number;
  className?: string;
  /** Rendered when no icon matches. Defaults to a small neutral square. */
  fallback?: React.ReactNode;
}

const LessonIcon: React.FC<Props> = ({ subject, grade, level, size = 40, className, fallback }) => {
  const resolvedLevel: SchoolLevel = level ?? levelFromGrade(grade);
  const src = lessonIconPath(subject, resolvedLevel);
  if (!src) {
    return (
      <>
        {fallback ?? (
          <div
            className={className}
            style={{ width: size, height: size, borderRadius: 8, background: '#eef2f9' }}
            aria-hidden="true"
          />
        )}
      </>
    );
  }
  return (
    <img
      src={src}
      alt={subject ?? ''}
      className={className}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
};

export default LessonIcon;
